import { Button } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';

import { AddManualLotEnabled, BulkLotUpdateEnabled } from 'common/utils/featureFlags';

import BulkLotUpdateDialog from './BulkLotUpdateDialog';
import { entryForProductAndFacility } from '../utils/bulkApplyPlan';
import {
  buildBulkLotUpdateChoices,
  findInitialBulkLotUpdateChoice,
  keepPickableBulkLotUpdateChoices,
} from '../utils/bulkLotUpdateChoices';
import { batchLotContextForRow } from '../utils/bulkManualLotEntry';

/**
 * POM-6537 — the "Bulk Lot Update" button beside Commit, and the dialog it opens.
 *
 * Offered only while some NDC has two or more rows a lot can be given to, at a facility with a lot
 * to pick or a way to record one by hand: with fewer rows each row's own pencil does the same, and
 * an offer with nothing behind it only raises the question of why. Apply stages through the
 * review's staging, so the rows land under Pending commit with "Undo bulk change", exactly as an
 * "Also apply" does.
 *
 * `batch` is useBatchLotClaims', `staging` useLotNdcChangeStaging's; `ndcFilter` is the grid's NDC
 * filter, which the dialog opens on when it names one of the choices.
 *
 * Hidden while BulkLotUpdateEnabled is off (the screen is being reworked); nothing is worked out then.
 */
const BulkLotUpdate = ({
  batch,
  payload,
  changes,
  staging,
  lotLookupCache,
  ndcFilter,
  todayStr,
  committing,
  onStaged,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { rows, unlistedRows } = batch.review;

  const availabilityFor = useCallback(
    (item) => entryForProductAndFacility(payload?.availability, item),
    [payload?.availability]
  );
  const recordableFor = useCallback(
    (item) => entryForProductAndFacility(payload?.recordableLots, item),
    [payload?.recordableLots]
  );
  const batchLotContextFor = useCallback(
    (item) => batchLotContextForRow({ items: payload?.items, changes, row: item }),
    [payload?.items, changes]
  );
  const choices = useMemo(
    () =>
      BulkLotUpdateEnabled
        ? keepPickableBulkLotUpdateChoices({
            choices: buildBulkLotUpdateChoices({ rows: [...rows, ...unlistedRows], changes }),
            availabilityFor,
            recordableFor,
            batchLotShareFor: batch.shareForRow,
            todayStr,
            isManualLotEnabled: AddManualLotEnabled,
          })
        : [],
    [rows, unlistedRows, changes, availabilityFor, recordableFor, batch.shareForRow, todayStr]
  );

  if (choices.length === 0) return null;

  const applyBulkLotUpdate = ({ bulkChanges }) => {
    staging.stageBulkApply({ bulkChanges });
    setIsOpen(false);
    onStaged();
  };

  return (
    <>
      <Button variant='outlined' disabled={committing} onClick={() => setIsOpen(true)}>
        Bulk Lot Update
      </Button>
      {isOpen && (
        <BulkLotUpdateDialog
          choices={choices}
          initialChoiceKey={findInitialBulkLotUpdateChoice({ choices, ndcFilter })?.key ?? null}
          availabilityFor={availabilityFor}
          recordableFor={recordableFor}
          batchLotContextFor={batchLotContextFor}
          batchLotShareFor={batch.shareForRow}
          planBulkApplyFor={batch.planBulkApplyFor}
          lotLookupCache={lotLookupCache}
          todayStr={todayStr}
          onApply={applyBulkLotUpdate}
          onCancel={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

BulkLotUpdate.propTypes = {
  batch: PropTypes.shape({
    review: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      unlistedRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
    shareForRow: PropTypes.func.isRequired,
    planBulkApplyFor: PropTypes.func.isRequired,
  }).isRequired,
  payload: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
    availability: PropTypes.arrayOf(PropTypes.shape({})),
    recordableLots: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  changes: PropTypes.objectOf(PropTypes.shape({})).isRequired,
  staging: PropTypes.shape({ stageBulkApply: PropTypes.func.isRequired }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  lotLookupCache: PropTypes.object,
  ndcFilter: PropTypes.string,
  todayStr: PropTypes.string.isRequired,
  committing: PropTypes.bool,
  /** After Apply staged the rows: the labels already built for the batch are stale. */
  onStaged: PropTypes.func.isRequired,
};

BulkLotUpdate.defaultProps = {
  payload: null,
  lotLookupCache: null,
  ndcFilter: null,
  committing: false,
};

export default BulkLotUpdate;
